/**
 * Analíticas de ventas: la misma pantalla que Analíticas, pero del dinero.
 *
 * El panel sabía enseñar el tráfico con tramos, comparación y tendencia, y del
 * comercio sólo tenía dos listas: Ofertas y Cobros. «¿Vendí más que el mes
 * pasado?», «¿qué se vende?», «¿los clientes vuelven?» eran preguntas que sólo
 * se podían responder contando filas a mano o saliendo a Stripe.
 *
 * Comparte de forma deliberada todo lo que se puede compartir con Analíticas
 * —el selector de fechas, las tarjetas de cifra, la gráfica, las listas y el
 * donut— para que las dos se lean igual: quien ya sabe leer una sabe leer la
 * otra sin aprender nada nuevo.
 *
 * Dos cosas que esta pantalla NO dice, y conviene tenerlas claras:
 *
 * - **Margen real.** «Ingresos netos» es lo cobrado menos lo devuelto. La
 *   comisión de Stripe no se descuenta porque no se guarda al cobrar: vive en el
 *   cargo y se le pide a Stripe al abrir un cobro, así que restarla aquí sería
 *   una llamada de red por cada venta del tramo. Y el coste de lo vendido no
 *   existe en el modelo — nadie lo ha declarado nunca.
 * - **Varias monedas.** Manda la que más facturó; el resto se enseña aparte, sin
 *   sumarse. Una gráfica no puede dibujar dos unidades en el mismo eje, y un
 *   total que mezcle pesos y dólares es un número que no significa nada.
 */
import { useEffect, useMemo, useState } from 'react'
import { SectionBar } from '../SectionBar'
import { CompareSelect, DateRangePicker, rangeLabel } from '../DateRangePicker'
import { MetricChart } from '../MetricChart'
import { MoneyMetric, panel, SplitDonut, StatCard, TopList, trend } from './analyticsShared'
import { analyticsQueryKey, getSalesAnalytics, type AnalyticsQuery, type BillingPlan, type SalesAnalytics } from '../../../lib/api'
import { fakeSalesAnalytics } from '../../../lib/fakeSalesAnalytics'
import { FeatureUpsell, ORDERS_UPSELL } from '../FeatureUpsell'
import { getCachedSalesAnalytics, setCachedSalesAnalytics } from '../../../lib/analyticsCache'
import { failureDetail, formatMoney } from './offerShared'
import { formatDashboardListDateWithTime } from '../../../lib/dashboardListDate'
import { useRegion } from '../../../lib/region'
import { useI18n } from '../../../lib/i18n'
import {
  IconArrowBackUp,
  IconCash,
  IconCoins,
  IconGavel,
  IconPackage,
  IconPlus,
  IconReceipt,
  IconRepeat,
  IconShoppingCart,
  IconUserPlus,
  IconUsers,
} from '@tabler/icons-react'


/**
 * Los colores del donut, atados al significado del segmento y no a su tamaño.
 *
 * La misma rampa verde monocroma que usan los dispositivos en Analíticas: dos o
 * tres pasos del mismo tono, nunca una paleta de colores distintos compitiendo.
 */
const INK = 'var(--dash-analytics-ink)'
const INK_2 = 'var(--dash-analytics-ink-2)'
const INK_3 = 'var(--dash-analytics-ink-3)'

/** Cómo se llama cada estado de venta en pantalla. */
/* La clave, no el rótulo: esto se arma al cargar el módulo. */
const STATUS_LABEL_KEY: Record<string, string> = {
  paid: 'salesAn.status.paid',
  partially_refunded: 'salesAn.status.partiallyRefunded',
  refunded: 'salesAn.status.refunded',
}
const STATUS_ORDER = ['paid', 'partially_refunded', 'refunded']
const STATUS_COLOR: Record<string, string> = {
  paid: INK,
  partially_refunded: INK_2,
  refunded: INK_3,
}

export function SalesAnalyticsSection({ projectId, token, plan, onOpenPlans, onCreateOffer, onOpenOffer, onOpenContact }: {
  projectId: string
  token: string
  /** Decide si se piden las ventas de verdad o se enseña la muestra. */
  plan: BillingPlan
  onOpenPlans: () => void
  /**
   * Crear la primera oferta, desde el vacío de esta pantalla.
   *
   * Sin nada vendido nunca, lo que falta no es un tramo de fechas distinto: es
   * algo que vender. Lleva al catálogo con la orden de abrir el alta al llegar
   * —el mismo camino que usa Analíticas de reservas para pedir una cita—, y no
   * abre nada aquí porque el asistente necesita lo que carga esa sección.
   */
  onCreateOffer?: (() => void) | undefined
  /** A la ficha de la oferta. Ver `TopListRow.onOpen`. */
  onOpenOffer?: ((offerId: string) => void) | undefined
  /** A la ficha del cliente en Contactos, por su identidad (`email:…`). */
  onOpenContact?: ((identity: string) => void) | undefined
}) {
  const i18n = useI18n()
  const { t, fmt, language } = i18n
  const { currency: projectCurrency } = useRegion()
  /**
   * Por defecto: últimos 30 días contra los 30 anteriores. La misma pregunta de
   * partida que en Analíticas — «¿voy mejor que el mes pasado?»— y no hay que
   * elegir nada para responderla.
   */
  const [query, setQuery] = useState<AnalyticsQuery>({ range: '30d', compare: 'previous' })
  const queryKey = analyticsQueryKey(query)
  /*
   * En el plan gratis esto no se pide: se enseña una muestra difuminada.
   *
   * La misma regla que en Analíticas de tráfico, y por el mismo motivo — quien
   * no paga nunca recibe cifras reales, ni siquiera en la pestaña de red. Ver
   * `fakeSalesAnalytics`.
   */
  const isPaid = plan !== 'free'
  const [realStats, setRealStats] = useState<SalesAnalytics | null>(() => (isPaid ? getCachedSalesAnalytics(projectId, queryKey) ?? null : null))
  const [realLoading, setRealLoading] = useState(() => isPaid && !getCachedSalesAnalytics(projectId, queryKey))
  const [error, setError] = useState<string | null>(null)
  const fake = useMemo(() => fakeSalesAnalytics(query.range === 'custom' ? '30d' : query.range, projectCurrency), [query.range, projectCurrency])
  const stats = isPaid ? realStats : fake
  const loading = isPaid ? realLoading : false

  useEffect(() => {
    if (!isPaid) { setRealLoading(false); return }
    let cancelled = false
    const cached = getCachedSalesAnalytics(projectId, queryKey)
    // Lo cacheado se enseña al instante; el esqueleto sólo en la primera carga
    // de verdad de este tramo. Cambiar de sección y volver no puede parpadear.
    if (cached) { setRealStats(cached); setRealLoading(false) } else { setRealLoading(true) }
    setError(null)
    void (async () => {
      try {
        const data = await getSalesAnalytics(token, projectId, query)
        if (cancelled) return
        setCachedSalesAnalytics(projectId, queryKey, data)
        setRealStats(data)
      } catch (err) {
        if (cancelled) return
        if (!cached) setRealStats(null)
        setError(failureDetail(err) || t('salesAn.loadFailed'))
      }
      if (!cancelled) setRealLoading(false)
    })()
    return () => { cancelled = true }
    // `queryKey` resume la consulta entera; `query` es un objeto nuevo en cada
    // render y dispararía el efecto sin parar.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, token, queryKey, isPaid])

  /** «3 ventas» / «1 venta»: el plural suelto se cuela en todas las listas. */
  const plural = (value: number, oneKey: string, manyKey: string) =>
    t(value === 1 ? oneKey : manyKey, { count: fmt.number(value) })
  const count = (value: number) => fmt.number(value)
  // Sin ventas todavía no hay moneda que deducir: se usa en la que cobra.
  const currency = stats?.currency ?? projectCurrency
  const totals = stats?.totals
  const cash = (amount: number) => formatMoney(amount, currency)
  /* Con la moneda en pequeño: en la cifra grande de la tarjeta, «USD» a
     tamaño de titular le comía el ancho al número y lo cortaba. */
  const cashMetric = (amount: number) => <MoneyMetric amount={amount} currency={currency} />

  /**
   * Cómo se nombra el tramo contra el que se mide. Con fechas concretas en vez
   * de «periodo anterior»: un porcentaje sin saber contra qué se calculó no se
   * puede juzgar.
   */
  const comparison = stats?.comparison
    ? query.compare === 'year'
      ? t('range.compare.year').toLocaleLowerCase(language)
      : rangeLabel(i18n, { range: 'custom', from: stats.comparison.from, to: stats.comparison.to })
    : null

  /*
   * Las fechas del tramo comparado viven en la «i» de cada tarjeta y ya no bajo
   * la cifra: son las mismas cuatro veces en la misma fila, y arriba está el
   * selector que las eligió. El pie conserva la diferencia absoluta, que es lo
   * que impide leer mal un porcentaje sobre números pequeños.
   */
  const comparedTo = comparison ? [{ term: t('analyticsCommon.comparedWith'), detail: comparison }] : []

  const series = (stats?.series ?? []).map((point) => ({
    day: point.day,
    ...(point.hour === undefined ? {} : { hour: point.hour }),
    primary: point.revenue,
    secondary: point.orders,
  }))
  const compareSeries = (stats?.comparison?.series ?? []).map((point) => ({
    day: point.day,
    primary: point.revenue,
    secondary: point.orders,
  }))

  const statusSlices = [
    ...STATUS_ORDER.map((status) => (stats?.statuses ?? []).find((row) => row.status === status)).filter((row) => !!row),
    ...(stats?.statuses ?? []).filter((row) => !STATUS_ORDER.includes(row.status)),
  ].map((row) => ({
    key: row.status,
    label: STATUS_LABEL_KEY[row.status] ? t(STATUS_LABEL_KEY[row.status]!) : row.status,
    value: row.orders,
    color: STATUS_COLOR[row.status] ?? INK_3,
  }))

  /* Sin ninguna venta en toda la vida del sitio no hay tramo que valga: cambiar
     las fechas seguiría enseñando ceros. Se dice una vez y se acaba la página. */
  const neverSold = !loading && !error && stats?.everSold === false

  const content = (
    <div className="dash-analytics-view" style={{ display: 'flex', flexDirection: 'column', gap: 24, paddingBottom: 64 }} aria-busy={loading}>
      <SectionBar title={t('salesAn.title')} subtitle={t('salesAn.subtitle')}>
        <div className="dash-toolbar dash-layered-toolbar dash-analytics-range-toolbar">
          <DateRangePicker
            query={query}
            window={stats?.window}
            timezone={stats?.timezone}
            onChange={setQuery}
          />
          <CompareSelect query={query} onChange={setQuery} />
        </div>
      </SectionBar>

      {error ? (
        <div className="dash-analytics-content-in dash-card-description" style={{ ...panel, padding: 40, textAlign: 'center' }}>
          {error}
        </div>
      ) : neverSold ? (
        <div className="dash-analytics-content-in dash-placeholder" style={panel}>
          <IconCoins size={34} aria-hidden="true" />
          <strong>{t('salesAn.emptyTitle')}</strong>
          <span>{t('salesAn.emptyBody')}</span>
          {onCreateOffer ? (
            <button type="button" className="dash-primary-btn" onClick={onCreateOffer}>
              <IconPlus size={15} aria-hidden="true" />
              {t('salesAn.emptyCta')}
            </button>
          ) : null}
        </div>
      ) : (
        <>
          {/* Lo que se está dejando fuera, dicho antes que las cifras y no
              debajo: quien cobra en dos monedas tiene que saber que el total de
              arriba es sólo de una ANTES de leerlo. */}
          {(stats?.otherCurrencies.length ?? 0) > 0 ? (
            <div className="dash-analytics-content-in dash-card-description" style={{ ...panel, padding: '12px 16px' }}>
              {t('salesAn.otherCurrencies.lead', { currency: currency.toUpperCase() })}{' '}
              {stats?.otherCurrencies.map((other, index) => (
                <span key={other.currency}>
                  {index > 0 ? ', ' : ''}
                  <strong>{other.formatted}</strong> {t('salesAn.otherCurrencies.in', { currency: other.currency.toUpperCase() })} ({plural(other.orders, 'salesAn.unit.saleOne', 'salesAn.unit.saleMany')})
                </span>
              ))}
              {t('salesAn.otherCurrencies.tail')}
            </div>
          ) : null}

          <div className="dash-auto-grid">
            <StatCard
              label={t('salesAn.revenue')}
              value={cashMetric(totals?.revenue ?? 0)}
              icon={IconCoins}
              change={totals ? trend(totals.revenue, totals.revenuePrev) : null}
              /* Sin ventas en el tramo, lo dice: una cifra en cero sola en la
                 caja no se distingue de una que aún no ha cargado. */
              hint={totals && totals.orders === 0 ? t('salesAn.noSales') : undefined}
              info={{ note: t('salesAn.revenue.note'), rows: comparedTo }}
              loading={loading}
            />
            <StatCard
              label={t('salesAn.orders')}
              value={count(totals?.orders ?? 0)}
              icon={IconShoppingCart}
              change={totals ? trend(totals.orders, totals.ordersPrev) : null}
              hint={totals && totals.orders === 0 ? t('salesAn.noSales') : undefined}
              info={{ note: t('salesAn.orders.note'), rows: comparedTo }}
              loading={loading}
            />
            <StatCard
              label={t('salesAn.aov')}
              value={cashMetric(totals?.aov ?? 0)}
              icon={IconReceipt}
              change={totals ? trend(totals.aov, totals.aovPrev) : null}
              hint={totals && totals.orders === 0 ? t('salesAn.noSales') : undefined}
              info={{ note: t('salesAn.aov.note'), rows: comparedTo }}
              loading={loading}
            />
            <StatCard
              label={t('salesAn.customers')}
              value={count(totals?.customers ?? 0)}
              icon={IconUsers}
              change={totals ? trend(totals.customers, totals.customersPrev) : null}
              /* Este sí se queda a la vista: es un dato, no una definición, y el
                 reparto nuevos/recurrentes no está en ninguna otra cifra. */
              /* Sin compradores, la frase de las listas vacías y no «0 new · 0
                 returning», que reparte una nada en dos. */
              hint={!totals ? undefined
                : totals.customers === 0 ? t('salesAn.noBuyers')
                : t('salesAn.customers.hint', { new: plural(totals.newCustomers, 'salesAn.unit.newOne', 'salesAn.unit.newMany'), returning: count(totals.returningCustomers) })}
              info={{ note: t('salesAn.customers.note'), rows: comparedTo }}
              loading={loading}
            />
          </div>

          <MetricChart
            series={series}
            comparison={compareSeries}
            comparisonLabel={comparison}
            granularity={stats?.granularity}
            timezone={stats?.timezone}
            loading={loading}
            title={t('salesAn.chart.title')}
            subtitle={stats?.granularity === 'hour' ? t('salesAn.chart.perHour') : t('salesAn.chart.perDay')}
            icon={<IconCash size={14} />}
            primaryLabel={t('salesAn.chart.revenue')}
            secondaryLabel={t('salesAn.chart.orders')}
            formatPrimary={cash}
            /* Abreviado sólo en el eje: ahí se repite tres veces y compite con
               la propia línea por el ancho. En el tooltip va entero. */
            formatAxis={(value) => formatMoney(value, currency, { compact: true })}
          />

          {/* Segunda fila: lo que matiza a la primera. Va debajo de la gráfica y
              no al lado de los totales porque son cifras de control —¿me están
              devolviendo mucho?, ¿los clientes repiten?— y no el titular. */}
          <div className="dash-auto-grid">
            <StatCard
              label={t('salesAn.gross')}
              value={cashMetric(totals?.gross ?? 0)}
              icon={IconCash}
              change={totals ? trend(totals.gross, totals.grossPrev) : null}
              hint={totals && totals.orders === 0 ? t('salesAn.noSales') : undefined}
              info={{ note: t('salesAn.gross.note'), rows: comparedTo }}
              loading={loading}
            />
            <StatCard
              label={t('salesAn.refunded')}
              value={cashMetric(totals?.refunded ?? 0)}
              icon={IconArrowBackUp}
              hint={!totals ? undefined
                : totals.orders === 0 ? t('salesAn.noSales')
                : plural(totals.refundedOrders, 'salesAn.unit.refundedOne', 'salesAn.unit.refundedMany')}
              info={{ note: t('salesAn.refunded.note') }}
              loading={loading}
            />
            <StatCard
              label={t('salesAn.newCustomers')}
              value={count(totals?.newCustomers ?? 0)}
              icon={IconUserPlus}
              hint={totals && totals.customers > 0
                ? t('salesAn.newCustomers.hint', { percent: Math.round((totals.newCustomers / totals.customers) * 100) })
                : t('salesAn.newCustomers.none')}
              info={{ note: t('salesAn.newCustomers.note') }}
              loading={loading}
            />
            <StatCard
              label={t('salesAn.disputes')}
              value={count(totals?.disputedOrders ?? 0)}
              icon={IconGavel}
              hint={totals && totals.disputedOrders > 0
                ? t('salesAn.disputes.hint', { amount: cash(totals.disputedAmount) })
                : t('salesAn.disputes.none')}
              info={{ note: t('salesAn.disputes.note') }}
              loading={loading}
            />
          </div>

          <div className="dash-auto-grid is-wide">
            <TopList
              title={t('salesAn.topOffers')}
              icon={IconPackage}
              rows={(stats?.topOffers ?? []).map((offer) => ({
                key: offer.offerId ?? `title:${offer.title}`,
                label: offer.title,
                sub: plural(offer.orders, 'salesAn.unit.saleOne', 'salesAn.unit.saleMany'),
                value: offer.formatted,
                image: offer.image,
                /* La oferta ya no existe, pero lo que facturó sí ocurrió: se
                   enseña marcado en vez de desaparecer del historial. */
                deleted: offer.offerId === null,
                deletedHint: t('salesAn.offerDeleted'),
                ...(onOpenOffer && offer.offerId ? { onOpen: () => onOpenOffer(offer.offerId!) } : {}),
              }))}
              empty={t('salesAn.noSales')}
              loading={loading}
            />
            <TopList
              title={t('salesAn.topCustomers')}
              icon={IconUsers}
              rows={(stats?.topCustomers ?? []).map((customer) => ({
                key: customer.email ?? customer.name ?? 'anonymous',
                label: customer.name ?? customer.email ?? t('salesAn.unnamed'),
                sub: customer.name && customer.email ? customer.email : plural(customer.orders, 'salesAn.unit.chargeOne', 'salesAn.unit.chargeMany'),
                value: customer.formatted,
                /* Por el correo, que es como Contactos junta a una persona: sin
                   correo no hay ficha a la que ir, y la fila se queda quieta. */
                ...(onOpenContact && customer.email
                  ? { onOpen: () => onOpenContact(`email:${customer.email!.toLocaleLowerCase()}`) }
                  : {}),
              }))}
              empty={t('salesAn.noBuyers')}
              loading={loading}
            />
            <TopList
              title={t('salesAn.recentSales')}
              icon={IconReceipt}
              rows={(stats?.recentSales ?? []).map((sale) => ({
                key: sale.id,
                label: sale.offerTitle,
                sub: `${sale.buyerName ?? sale.buyerEmail ?? t('salesAn.unnamed')} · ${formatDashboardListDateWithTime(sale.createdAt)}`,
                value: sale.formatted,
                ...(onOpenOffer && sale.offerId ? { onOpen: () => onOpenOffer(sale.offerId!) } : {}),
              }))}
              empty={t('salesAn.noSales')}
              loading={loading}
            />
            {/* Único contra recurrente: es lo que distingue un sitio que cobra
                una vez de uno que cobra todos los meses, y no se ve en ninguna
                otra pantalla — Cobros los mezcla en la misma lista. */}
            <SplitDonut
              title={t('salesAn.chargeType')}
              icon={IconRepeat}
              slices={[
                { key: 'one_time', label: t('salesAn.oneTime'), value: totals?.oneTimeRevenue ?? 0, color: INK },
                { key: 'subscription', label: t('salesAn.subscription'), value: totals?.subscriptionRevenue ?? 0, color: INK_2 },
              ]}
              empty={t('salesAn.noCharges')}
              unit={t('salesAn.unit.ofRevenue')}
              formatValue={cash}
              loading={loading}
            />
            <SplitDonut
              title={t('salesAn.newVsReturning')}
              icon={IconUserPlus}
              slices={[
                { key: 'new', label: t('salesAn.new'), value: totals?.newCustomers ?? 0, color: INK },
                /* «Recurrente» y no «Ya habían comprado»: la etiqueta convive con
                   «Nuevos» en la leyenda de un sector y en el rótulo bajo el
                   porcentaje, y a esos anchos la frase larga se cortaba. Además
                   hace pareja con «Nuevos» —una palabra cada una—, que es como se
                   leen dos sectores de un mismo gráfico. */
                { key: 'returning', label: t('salesAn.returning'), value: totals?.returningCustomers ?? 0, color: INK_2 },
              ]}
              empty={t('salesAn.noBuyers')}
              unit={t('salesAn.unit.customers')}
              loading={loading}
            />
            {/* Sólo cuando hay algo que no sea «pagada»: un donut de un único
                segmento al 100% no informa de nada y ocupa una tarjeta entera. */}
            {statusSlices.filter((slice) => slice.value > 0).length > 1 ? (
              <SplitDonut
                title={t('salesAn.statusTitle')}
                icon={IconArrowBackUp}
                slices={statusSlices}
                empty={t('salesAn.noSales')}
                unit={t('salesAn.unit.sales')}
                loading={loading}
              />
            ) : null}
          </div>
        </>
      )}
    </div>
  )

  /* Igual que Analíticas de tráfico: lo mismo que se ve pagando, difuminado y
     con la oferta encima. Se enseña la pantalla entera —no un cartel vacío—
     porque lo que se vende es precisamente cómo se ve esto lleno. */
  return isPaid ? content : <FeatureUpsell onOpenPlans={onOpenPlans} variant="full" copy={ORDERS_UPSELL}>{content}</FeatureUpsell>
}
